import { gql } from '@apollo/client';
import { Box, Button, chakra, Link, Stack, VStack } from '@chakra-ui/react';
import { t } from 'i18next';
import { useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { SubscriptionsTableV2 } from '~components/organization/SubscriptionsTableV2/SubscriptionsTable';
import { useSubscriptionsTableV2 } from '~components/organization/SubscriptionsTableV2/useSubscriptionsTable';
import UpgradeToWaveEssentialEmptyState from '~components/organization/UpgradeToWaveEssentialModal/EmptyState';
import { ExternalLinkIcon } from '~components/ui/icons';
import { VerticalTabContent } from '~components/ui/VerticalTabs';
import { useConditionalPolling } from '~components/useConditionalPolling';
import { useFeatureFlag } from '~utils/features';
import { useOrganizationSubscriptionsV2IndexPageQuery } from './__generated__/Index.graphql';

const POLL_INTERVAL = 10000;

export function OrganizationSubscriptionsV2IndexPage() {
  const { isEnabled: isAppDirectMarketplaceLinkEnabled } = useFeatureFlag(
    'appDirectMarketplaceLink',
  );

  const { data, loading, error, startPolling, stopPolling } =
    useOrganizationSubscriptionsV2IndexPageQuery({
      pollInterval: POLL_INTERVAL,
    });

  useConditionalPolling({ startPolling, stopPolling, pollInterval: POLL_INTERVAL });

  const subscriptions = useMemo(
    () => data?.organization.waveSubscriptions ?? [],
    [data?.organization.waveSubscriptions],
  );

  const [hideExpired, setHideExpired] = useState(false);

  const table = useSubscriptionsTableV2(subscriptions, hideExpired);

  const handleHideExpiredChange = (checked: boolean) => {
    setHideExpired(checked);
    table.gotoPage(0);
  };

  return (
    <VerticalTabContent title={t('subscriptions')} isLoading={loading} error={error}>
      <Stack direction={{ sm: 'column', md: 'row' }} spacing={{ sm: 6, md: 36 }} mb="10">
        <VStack alignItems="stretch" spacing={6}>
          <chakra.p fontSize="md" color="gray.500">
            <Trans
              i18nKey="contactSupportSubscription"
              components={[
                <chakra.a
                  key="doc-link"
                  href={process.env.REACT_APP_WAVE_DOCS_LINK ?? ''}
                  target="_blank"
                  rel="noopener noreferrer"
                  textDecoration="underline"
                />,
              ]}
            />
          </chakra.p>
          <chakra.p fontSize="md" color="gray.500">
            {t('purchaseSubscriptions')}
          </chakra.p>
        </VStack>
        {isAppDirectMarketplaceLinkEnabled && (
          <Link
            href={process.env.REACT_APP_APPDIRECT_MARKETPLACE_LINK}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              textDecoration: 'none',
            }}
          >
            <Button variant="solid" colorScheme="blue">
              <Stack direction="row">
                <chakra.span textDecoration="none">{t('purchaseProStore')}</chakra.span>
                <ExternalLinkIcon />
              </Stack>
            </Button>
          </Link>
        )}
      </Stack>
      {subscriptions.length === 0 ? (
        <Box
          boxShadow="elevated"
          border="1px solid"
          borderRadius="md"
          borderColor="gray.100"
          overflowX="auto"
          background="white"
          paddingX="10"
        >
          <UpgradeToWaveEssentialEmptyState externalLink noSubscriptions />
        </Box>
      ) : (
        <>
          <SubscriptionsTableV2.HideExpired
            isChecked={hideExpired}
            onChange={handleHideExpiredChange}
          />
          <Box
            boxShadow="elevated"
            border="1px solid"
            borderRadius="md"
            borderColor="gray.100"
            overflowX="auto"
            bgColor="white"
            width={{ base: '500px', md: 'full', lg: 'full' }}
          >
            <SubscriptionsTableV2 table={table} />
          </Box>
          <Box display="flex" marginTop="4">
            <Box flex="1">
              <SubscriptionsTableV2.PageSizeSelector table={table} />
            </Box>
            <SubscriptionsTableV2.Pagination table={table} />
          </Box>
        </>
      )}
    </VerticalTabContent>
  );
}

OrganizationSubscriptionsV2IndexPage.graphql = {
  queries: {
    OrganizationSubscriptionsV2IndexPage: gql`
      query OrganizationSubscriptionsV2IndexPage {
        organization {
          id
          waveSubscriptions {
            ...SubscriptionsTableV2Subscription
          }
        }
      }
    `,
  },
};
